import { sendGAEvent, sendGALinkClickEvent } from '@noths/polaris-client-google-analytics';

import type { TrackingContext } from 'src/types/TrackingContext';

export interface HeroVideoTracking extends TrackingContext {
  videoTitle: string;
}

// Same lowercase GA4 shape as videoBlock.ts so the two video blocks report side by side.
export const trackHeroVideoImpression = ({
  trackingPosition,
  trackingTitle,
  videoTitle,
}: HeroVideoTracking) => {
  sendGAEvent({
    event: 'custom_event',
    event_action: 'Impression',
    event_category: `${trackingPosition} | Hero video`,
    event_label: `${trackingPosition} | ${trackingTitle} | ${videoTitle}`.toLowerCase(),
    event_name: 'view_content',
    content_position: `${trackingPosition}`,
    content_type: `hero video|${trackingTitle}`.toLowerCase(),
    video_title: `${videoTitle}`.toLowerCase(),
  });
};

/** Where the CTA was clicked: under the preview loop, or on the end screen after the full film. */
export type HeroVideoCtaPlacement = 'inline' | 'end screen';

// The CTA is a link, so the event is sent before navigation (sendGALinkClickEvent defers it) or
// the hit would be lost when the page unloads.
export const trackHeroVideoCtaClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  { trackingPosition, trackingTitle, videoTitle }: HeroVideoTracking,
  rawButtonText: string,
  placement: HeroVideoCtaPlacement,
) => {
  // Content editors leave trailing spaces in button text; keep GA labels clean.
  const buttonText = rawButtonText.trim();
  const action = placement === 'end screen' ? 'End screen CTA click' : 'CTA click';

  sendGALinkClickEvent(e, {
    event: 'custom_event',
    event_action: action,
    event_category: `${trackingPosition} | Hero video`,
    event_label: `${trackingPosition} | ${trackingTitle} | ${buttonText}`.toLowerCase(),
    event_name: 'content_interaction',
    event_details: `${action}|${buttonText}`.toLowerCase(),
    content_position: `${trackingPosition}`,
    content_type: `hero video|${trackingTitle}`.toLowerCase(),
    video_title: `${videoTitle}`.toLowerCase(),
  });
};
